import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { BatchGetThreadStatusIO } from '../types';

const hashRequest = ({
  personPhone,
  locationPhone,
  locationId,
}: Omit<BatchGetThreadStatusIO['input']['requests'][number], 'threadId'>): string =>
  `${personPhone}|${locationId}|${locationPhone}`;

const convertToArray = (map: Record<string, string[]>): { locationId: string; locationPhones: string[] }[] => {
  const entries = Object.entries(map);
  return entries.map(([locationId, locationPhones]) => ({ locationId, locationPhones }));
};

export type CalculatePossibleThreadStatusRequestsArgs = {
  personPhones: string[];
  locationPhonesByLocation: { locationId: string; locationPhones: string[] }[] | Record<string, string[]>;
};

/**
 * Takes in various lists that map to parts of a `BatchGetThreadStatus` request and returns every possible combination
 */
export const calculatePossibleThreadStatusRequests = ({
  personPhones,
  locationPhonesByLocation,
}: CalculatePossibleThreadStatusRequestsArgs): BatchGetThreadStatusIO['input']['requests'] => {
  const normalizedPersonPhones = personPhones.map(formatPhoneNumberE164);
  const locationPhonesByLocationArray = Array.isArray(locationPhonesByLocation)
    ? locationPhonesByLocation
    : convertToArray(locationPhonesByLocation);
  return locationPhonesByLocationArray.reduce<{
    requestHashes: Set<string>;
    requests: BatchGetThreadStatusIO['input']['requests'];
  }>(
    (acc, { locationId, locationPhones }) => {
      const normalizedLocationPhones = locationPhones.map(formatPhoneNumberE164);
      normalizedLocationPhones.forEach((locationPhone) => {
        normalizedPersonPhones.forEach((personPhone) => {
          const hash = hashRequest({ personPhone, locationId, locationPhone });
          if (!acc.requestHashes.has(hash)) {
            acc.requests.push({ personPhone, locationId, locationPhone });
            acc.requestHashes.add(hash);
          }
        });
      });
      return acc;
    },
    { requestHashes: new Set<string>(), requests: [] }
  ).requests;
};
